import { CalculationForm } from "features/calculation/CalculationForm";
import { useFeeBasisAsTransactionCalculationCreate } from "gen/endpoints/fee-basis/fee-basis";
import { useFeeBasisCategoryAsTransactionCalculationCreate } from "gen/endpoints/fee-basis-category/fee-basis-category";
import { useTransactionCalculationCreate } from "gen/endpoints/transactions/transactions";
import { CalculationTypeEnum } from "gen/model/calculationTypeEnum";
import { TransactionCalculationPayloadCreateDataRequest } from "gen/model/transactionCalculationPayloadCreateDataRequest";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const CalculationCreatePage: React.FC = () => {
  const { t } = useTranslation(["calculation"]);

  const { fundFamilyId } = useRequiredStringParams();

  const { mutateAsync: createCalculation } = useTransactionCalculationCreate();
  const { mutateAsync: createFeeBasisCategory } =
    useFeeBasisCategoryAsTransactionCalculationCreate();

  const { mutateAsync: createFeeBasis } =
    useFeeBasisAsTransactionCalculationCreate();

  const onSubmitForCalculation = useCallback(
    (data: TransactionCalculationPayloadCreateDataRequest) =>
      createCalculation({ fundFamilyId, data }),
    [createCalculation, fundFamilyId],
  );

  const onSubmitForFeeBasisCategory = useCallback(
    (data: TransactionCalculationPayloadCreateDataRequest) =>
      createFeeBasisCategory({
        fundFamilyId,
        data,
      }),
    [createFeeBasisCategory, fundFamilyId],
  );

  const onSubmitForFeeBasis = useCallback(
    (data: TransactionCalculationPayloadCreateDataRequest) =>
      createFeeBasis({
        fundFamilyId,
        data,
      }),
    [createFeeBasis, fundFamilyId],
  );

  const onSubmit = async (
    data: TransactionCalculationPayloadCreateDataRequest,
  ) => {
    if ((data.classification as string) === "fee_basis") {
      await onSubmitForFeeBasis(data);
    } else if ((data.classification as string) == "fee_basis_category") {
      await onSubmitForFeeBasisCategory(data);
    } else {
      await onSubmitForCalculation(data);
    }
  };

  return (
    <PageContent>
      <PageHeader size="lg" title={t("calculation:setUpCalculation")} />
      <PageBody>
        <CalculationForm
          fundFamilyId={fundFamilyId}
          onSubmit={onSubmit}
          defaultValues={{
            fund_family_id: fundFamilyId,
            calculation_type: CalculationTypeEnum.advanced,
          }}
        />
      </PageBody>
    </PageContent>
  );
};
